/**
 * Pillar-to-route mapping helper.
 *
 * Maps the current pathname to a Leviathan brand pillar so that layout
 * chrome (sidebar, page header, top bar) can tint itself accordingly.
 *
 * Pillar assignment rationale:
 *  - leviathan (gold)      : quantitative core, screening, portfolio, factors
 *  - ouroboros (purple)    : deep research, qualitative AI analysis, ML insights
 *  - apophis (teal)        : output, compliance, jobs, analytics, deliverables
 *  - crossCutting (slate)  : auth, settings, profile, design tools, 404
 *
 * Prefix matching is done with trailing-slash boundaries so that e.g.
 * `/research-internal` does not match `/research`.
 *
 * Do not add em-dashes anywhere.
 */

import type { Pillar } from '@/design-system/tokens'

/** True when `p` equals `prefix` or starts with `prefix + "/"`. */
function underPrefix(p: string, prefix: string): boolean {
  return p === prefix || p.startsWith(prefix + '/')
}

/**
 * Returns the pillar for the given pathname.
 * Matching is prefix-first and longest-match wins within each pillar list.
 */
export function getPillarForRoute(pathname: string): Pillar {
  const p = pathname.toLowerCase()

  // CrossCutting: check these FIRST so they win over more general rules.
  // (design-system-preview lives under /devtools but is cross-cutting, not apophis.)
  if (
    p === '/login' ||
    p === '/register' ||
    p === '/forgot-password' ||
    p === '/reset-password' ||
    p === '/verify-email' ||
    p === '/settings' ||
    p === '/profile' ||
    p === '/404' ||
    underPrefix(p, '/devtools/design-system-preview') ||
    p === '/'
  ) {
    return 'crossCutting'
  }

  // Ouroboros: research, ML insights
  if (underPrefix(p, '/research') || underPrefix(p, '/ml-insights')) {
    return 'ouroboros'
  }

  // Apophis: output layer (apophis, jobs, compliance, analytics, calibration, devtools, deliverables)
  if (
    underPrefix(p, '/apophis') ||
    underPrefix(p, '/jobs') ||
    underPrefix(p, '/compliance') ||
    underPrefix(p, '/calibration') ||
    underPrefix(p, '/analytics') ||
    underPrefix(p, '/deliverables') ||
    underPrefix(p, '/devtools')
  ) {
    return 'apophis'
  }

  // Leviathan: everything else (dashboard, analysis, screen, compare, mna, manual-input,
  // portfolio, watchlist, events, estimates, factors, backtesting, strategies, moat,
  // credit-universe, crowded-trades, batch, portfolio-risk)
  return 'leviathan'
}
